<template>
    <VJoyDialog
        id="removalConfirmDialog"
        ref="removeDialog"
        pattern-confirm
        :confirm-text="t('general.continue')"
        :cancel-text="t('general.cancel')"
        @dialog:confirm="removeEducation"
    >
        <template #dialog-header>
            <p slot="dialog-header">{{ $t('generic.form.confirm.text') }}</p>
        </template>
    </VJoyDialog>
    <Draggable
        v-model="educationSectionState"
        class="profile-educations__list"
        :group="{name: 'educations', put: true, pull: true}"
        draggable=".draggable-education"
        handle=".draggable-handle"
        tag="ul"
        @start="dragging = true"
        @end="dragging = false"
        @update="dragged"
    >
        <EducationFragment
            v-for="element in educationSectionState"
            :key="`education--${element.id}`"
            :model-value="element"
            :is-edit-mode="isEditionEnabled"
            :is-sample="false"
            :remove-dialog-ref="removeDialog"
            @remove="(id) => (idx = id)"
            @edit="(id) => openEducationsEditDialog(id)"
        />
    </Draggable>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {VueDraggable as Draggable} from 'vue-draggable-plus';
    import {ref, useProfilePageViewMode, useTranslation} from '#imports';
    import {VJoyDialog} from '@maltjoy/core-vue';
    import EducationFragment from '~/components/main/educations/EducationFragment.vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useEducationSectionState} from '~/features/education/composables/useEducationSectionState';
    import {useProfileStore} from '~/store/profileStore';
    import {educationMapper} from '~/types/infra/EducationMapper';

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();

    // State
    const educationSectionState = useEducationSectionState();

    // Stores
    const {updateEducations} = useProfileStore();
    const {educations} = storeToRefs(useProfileStore());

    // Refs
    const removeDialog = useTemplateRef('removeDialog');
    const idx = ref(0);
    const dragging = ref<boolean>(false);

    async function removeEducation() {
        const currentEducations = educations.value;
        currentEducations?.items.splice(idx.value, 1);

        const educationRequests = currentEducations?.items.map((education, index) => educationMapper.toRequest(education, index));
        if (educationRequests) {
            await updateEducations({educations: educationRequests});
            removeDialog.value?.hide();
            eventBus.emit('educations:deleted');
        }
    }

    function openEducationsEditDialog(educationId: number) {
        if (isEditionEnabled.value) {
            eventBus.emit('educations:edit-mode', {educationId, entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
        }
    }

    async function dragged() {
        if (educationSectionState.value.length > 0) {
            const displayedEducations = educationSectionState.value;
            const displayedEducationIds = new Set(displayedEducations.map((education) => education.id));
            const remainingEducations =
                educations.value?.items
                    .map((education, index) => educationMapper.toRequest(education, index))
                    .filter((education) => !displayedEducationIds.has(education.id)) ?? [];
            const updateEducationsPayload = [...displayedEducations, ...remainingEducations];

            await updateEducations({educations: updateEducationsPayload});
        }
    }
</script>

<style lang="scss" scoped>
    .profile-educations__list {
        list-style-type: none;
    }
</style>
